import { useMemo } from "react"

import {
  adaptationLabels,
  ADAPTATION_ANSWER_ORDER,
  type PreorderDevelopment,
} from "~/components/demand-2/shared/preorderDevelopment"
import {
  pinnedAxisMax,
  signedPct,
  signedUnits,
} from "~/components/demand-2/shared/demandBreakdownShared"
import {
  KnownShareBar,
  type KnownShareCopy,
  knownShareSeries,
  PREORDER_WEEKLY_Y_AXES,
  PreorderAdaptationQuestion,
  PreorderChartPanel,
} from "~/components/demand-2/shared/PreorderBreakdownLayout"
import { SizingSpinner } from "~/components/demand-2/shared/SizingSpinner"
import SuperChart, {
  type DateChartSeries,
  type StackedBarChartData,
} from "~/components/reusable/graphs/SuperChart"
import { useChartBaseColors, useChartSeriesConfig } from "~/constants/colors"
import { ChartType, DateType, LegendAlignment, TodayPlotLine } from "~/constants/graphs"
import { type AdaptationMode } from "~/utils/demandPreorderDevelopment"
import { PLAN_PERIOD_WEEKS } from "~/utils/demandPlanPeriod"
import { formatNumber } from "~/utils/formatting"
import { useAllSlicesGuard } from "~/components/demand-2/shared/useAllSlicesGuard"
import { useDemand2Scope } from "~/components/demand-2/useDemand2Scope"
import { isNarrowScope } from "~/utils/demandFraming"
import { formatName } from "~/utils/namingUtils"

/**
 * The NOOS "Pre-order development" breakdown, laid out and coloured as fashion's
 * (`FashionPreorderDevelopmentBreakdown`). Its figures are the engine's books, off the weeks the
 * baseline reads (`usePreorderDevelopment`), so the chart draws exactly what the insight is sized on.
 */

/**
 * The weeks the books cover: the next 52 when any variant is in the whole plan, else the weeks they
 * share, without a count when they differ.
 */
const planWeeksPhrase = (weeksInPlan: number | null): string => {
  if (weeksInPlan == null) return "weeks in the plan"
  if (weeksInPlan === PLAN_PERIOD_WEEKS) return `next ${PLAN_PERIOD_WEEKS} weeks`
  return `${formatNumber(weeksInPlan)} ${weeksInPlan === 1 ? "week" : "weeks"} in the plan`
}

/** A function, not a constant: `formatName` reads tenant naming at call time. */
const noosAdaptationNotes = (weeks: string): Record<AdaptationMode, string> => {
  const preorders = formatName("preorder", true)
  return {
    closeBook: `Expect no further ${preorders} on top of what is confirmed`,
    maintain: `Assume the ${weeks} land where the baseline did`,
    adaptChange: `Expect the ${preorders} still to come to move by as many units as the book has against the baseline`,
    adaptTrend: `Expect the ${preorders} still to come to follow the book's development against the baseline`,
  }
}

const noosKnownShareCopy = (weeks: string): KnownShareCopy => ({
  label: `Share of the ${weeks} confirmed`,
  resetTitle: (estimated) => `Back to the estimated ${estimated}`,
})

const PreorderDevelopmentBreakdown = ({
  persistKey,
  data,
}: {
  persistKey: string
  data: PreorderDevelopment
}) => {
  const {
    totals,
    weeks,
    diff,
    diffPct,
    projected,
    projectedByMode,
    measuredProjectedByMode,
    knownShare,
    measuredKnownShare,
    overrideShare,
    development,
    currentWeek,
    mode,
    modeAnswered,
    modeInherited,
    scope,
    clearMode,
    setKnownShareOverride,
    weeksInPlan,
  } = data
  const chartBaseColors = useChartBaseColors()
  const seriesConfig = useChartSeriesConfig()
  // The route's first collection, the id every answer is written against (`useAdaptationAnswer`).
  const { collectionIds } = useDemand2Scope()
  const guard = useAllSlicesGuard({
    question: "preorderDevelopment",
    collectionId: collectionIds[0] ?? 0,
    scope,
    // The picker's own wording, which differs by kind, not the generic option label.
    labelFor: (answer) => adaptationLabels("noos")[answer as AdaptationMode] ?? null,
  })

  const preorders = formatName("preorder", true)
  const planWeeks = planWeeksPhrase(weeksInPlan)
  const columns = useMemo(
    () => [`Baseline ${preorders}`, "Expected by now", "Confirmed now", `Projected ${planWeeks}`],
    [preorders, planWeeks],
  )
  // Readable only with a book a year ago to compare against.
  const paceReadable = totals.knownAYearAgo > 0
  const paceLabel = paceReadable
    ? `${signedUnits(diff)}${diffPct == null ? "" : ` (${signedPct(diffPct)})`} vs expected`
    : null

  const series = useMemo(
    (): StackedBarChartData[] => [
      {
        name: `Confirmed ${preorders}`,
        type: "column",
        color: seriesConfig.confirmedPreOrders.color,
        // The last column's base is capped at the projection, so its segments sum to it even when
        // an answer projects below the book.
        data: [
          Math.round(totals.lastYear),
          Math.round(totals.knownAYearAgo),
          Math.round(totals.knownNow),
          Math.round(Math.min(totals.knownNow, projected)),
        ],
        pointLabels: [null, null, paceLabel, null],
      },
      {
        name: `Expected ${preorders}`,
        type: "column",
        color: seriesConfig.expectedPreOrders.color,
        data: [null, null, null, Math.round(Math.max(development, 0))],
      },
    ],
    [totals, projected, development, paceLabel, preorders, seriesConfig],
  )

  // Pinned to the tallest column any answer produces on the estimated share, so switching answers or
  // dragging the share moves only the last one.
  const yAxisMax = useMemo(
    () =>
      pinnedAxisMax(
        Math.max(
          totals.lastYear,
          totals.knownAYearAgo,
          totals.knownNow,
          ...ADAPTATION_ANSWER_ORDER.map((m) => measuredProjectedByMode[m]),
        ),
      ),
    [totals, measuredProjectedByMode],
  )

  const weeklySeries = useMemo((): DateChartSeries[] => {
    if (weeks.length === 0) return []
    // The expected units placed on the weeks short of the baseline, scaled to the answer's total.
    const factor = totals.lastYear > 0 ? projected / totals.lastYear : 1
    const shortfalls = weeks.map((w) => Math.max(w.lastYear * factor - w.knownNow, 0))
    const shortfallTotal = shortfalls.reduce((sum, v) => sum + v, 0)
    const scale = shortfallTotal > 0 ? Math.max(development, 0) / shortfallTotal : 0
    return [
      {
        name: `Confirmed ${preorders}`,
        type: "column",
        colors: [seriesConfig.confirmedPreOrders.color],
        stack: "projected",
        stacking: "normal",
        data: weeks.map((w) => ({ milliseconds: w.ms, value: Math.round(w.knownNow) })),
      },
      {
        name: `Expected ${preorders}`,
        type: "column",
        colors: [seriesConfig.expectedPreOrders.color],
        stack: "projected",
        stacking: "normal",
        data: weeks.map((w, i) => ({
          milliseconds: w.ms,
          value: Math.round((shortfalls[i] ?? 0) * scale),
        })),
      },
      {
        name: `Baseline ${preorders}`,
        type: "spline",
        dashStyle: "Dash",
        colors: [chartBaseColors.contrast],
        data: weeks.map((w) => ({ milliseconds: w.ms, value: Math.round(w.lastYear) })),
      },
      knownShareSeries(
        "Known ratio",
        chartBaseColors.base,
        weeks.map((w) => ({ ms: w.ms, share: w.knownShare })),
        `Share of each week's ${preorders} normally confirmed this far ahead`,
      ),
    ]
  }, [weeks, totals.lastYear, projected, development, preorders, chartBaseColors, seriesConfig])

  if (data.isLoading) return <SizingSpinner className="h-[420px] w-full" />
  if (totals.lastYear <= 0 && totals.knownNow <= 0) {
    return (
      <p className="paragraph-sm text-fg-subtle">
        No {preorders} on this baseline, and nothing confirmed yet.
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-3">
        <KnownShareBar
          knownShare={knownShare}
          measuredKnownShare={measuredKnownShare}
          overrideShare={overrideShare}
          onChange={setKnownShareOverride}
          readOnly={scope.kind === "variant"}
          copy={noosKnownShareCopy(planWeeks)}
          lockedReason={
            mode === "closeBook"
              ? `"${adaptationLabels("noos").closeBook}" treats every ${formatName("preorder", false)} as confirmed already.`
              : undefined
          }
        />
        <PreorderChartPanel
          persistKey={persistKey}
          idPrefix="noos-preorder"
          series={series}
          columns={columns}
          yAxisMax={yAxisMax}
          weekly={
            weeklySeries.length === 0 ? null : (
              <SuperChart
                key="weekly"
                type={ChartType.TimeSeries}
                data={weeklySeries}
                isStacked
                height={340}
                legendAlignment={LegendAlignment.Bottom}
                dateType={DateType.Weekly}
                todayPlotLine={TodayPlotLine.Detailed}
                todayPlotLineValue={currentWeek.getTime()}
                yAxis={PREORDER_WEEKLY_Y_AXES}
              />
            )
          }
        />
      </section>
      <PreorderAdaptationQuestion
        kind="noos"
        idPrefix="noos-preorder"
        modes={ADAPTATION_ANSWER_ORDER}
        knownShare={knownShare}
        notes={noosAdaptationNotes(planWeeks)}
        baseline={totals.lastYear}
        projectedByMode={projectedByMode}
        mode={mode}
        modeAnswered={modeAnswered}
        modeInherited={modeInherited}
        narrow={isNarrowScope(scope)}
        paceNote={
          paceReadable
            ? null
            : `Nothing was confirmed at this point on the baseline, so the book can't be read as a trend: "Follow the trend" lands where "Match baseline" does.`
        }
        setMode={guard.write}
        clearMode={clearMode}
      />
      {guard.dialog}
    </div>
  )
}

export default PreorderDevelopmentBreakdown
